/* ET Blog — shared behaviour for the ET blog overview page.
   Syncs the two [data-et-blog-search] forms, live-searches articles into
   [data-et-blog-grid], keeps [data-et-blog-count] and search pagination in
   sync. Vanilla, IIFE, no globals, no dependencies. */
(function () {
  'use strict';

  var DEBOUNCE_MS = 250;
  var MIN_CHARS = 2;
  var VIEW = 'et-blog-cards';
  var LOADING_CLASS = 'is-et-blog-loading';
  var INPUT_SELECTOR = '[data-et-blog-search] input[name="q"]';

  var timer = null;
  var controller = null;
  var requestId = 0;
  var searching = false;
  var bound = false;
  var cache = { node: null, html: '', total: '', count: '' };

  function grid() { return document.querySelector('[data-et-blog-grid]'); }
  function countEl() { return document.querySelector('[data-et-blog-count]'); }
  function inputs() { return document.querySelectorAll(INPUT_SELECTOR); }

  function capture() {
    var el = grid();
    if (!el) return;
    if (cache.node !== el) {
      cache.node = el;
      cache.html = el.innerHTML;
      cache.total = el.getAttribute('data-et-blog-total') || '';
      cache.count = '';
      searching = false;
      el.setAttribute('aria-live', 'polite');
      el.setAttribute('aria-busy', 'false');
    }
    if (!cache.count) {
      var c = countEl();
      if (c) cache.count = c.textContent;
    }
  }

  function attr(name, fallback) {
    var el = grid();
    var value = el ? el.getAttribute(name) : null;
    return value ? value : fallback;
  }

  function endpointFor(href, term) {
    var url = new URL(href, window.location.origin);
    if (term !== null) {
      url.searchParams.set('q', term);
      url.searchParams.set('type', 'article');
    }
    url.searchParams.set('view', attr('data-et-blog-view', VIEW));
    return url.toString();
  }

  function setQuery(term) {
    if (!window.history || !window.history.replaceState) return;
    var url = new URL(window.location.href);
    if (term) { url.searchParams.set('q', term); } else { url.searchParams.delete('q'); }
    window.history.replaceState(window.history.state, '', url.toString());
  }

  function setLoading(on) {
    var el = grid();
    if (!el) return;
    el.classList.toggle(LOADING_CLASS, on);
    el.setAttribute('aria-busy', on ? 'true' : 'false');
  }

  function syncInputs(source) {
    var list = inputs();
    for (var i = 0; i < list.length; i++) {
      if (list[i] !== source && list[i].value !== source.value) list[i].value = source.value;
    }
  }

  function updateCount(from) {
    var c = countEl();
    if (!c || !from) return;
    var total = from.getAttribute('data-et-blog-total');
    if (total === null) return;
    var suffix = c.getAttribute('data-et-blog-count-suffix') || '';
    c.textContent = suffix ? total + ' ' + suffix : total;
  }

  function swap(next, scroll) {
    var el = grid();
    if (!el || !next) return;
    el.innerHTML = next.innerHTML;
    var total = next.getAttribute('data-et-blog-total');
    if (total !== null) el.setAttribute('data-et-blog-total', total);
    updateCount(el);
    if (scroll && el.scrollIntoView) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  function request(url, scroll) {
    if (controller) controller.abort();
    controller = window.AbortController ? new AbortController() : null;
    var id = ++requestId;
    var options = { headers: { 'X-Requested-With': 'XMLHttpRequest' } };
    if (controller) options.signal = controller.signal;
    setLoading(true);
    window.fetch(url, options)
      .then(function (res) { return res.ok ? res.text() : Promise.reject(); })
      .then(function (html) {
        if (id !== requestId) return;
        var doc = new DOMParser().parseFromString(html, 'text/html');
        swap(doc.querySelector('[data-et-blog-grid]'), scroll);
      })
      .catch(function () {})
      .then(function () { if (id === requestId) setLoading(false); });
  }

  function runSearch(term) {
    var base = attr('data-et-blog-search-url', null);
    if (!base) return; // no route published -> leave the native form GET alone
    searching = true;
    setQuery(term);
    request(endpointFor(base, term), false);
  }

  function restore() {
    if (controller) { controller.abort(); controller = null; }
    requestId++;
    searching = false;
    setLoading(false);
    var el = grid();
    if (!el || cache.node !== el) return;
    el.innerHTML = cache.html;
    if (cache.total) el.setAttribute('data-et-blog-total', cache.total);
    var c = countEl();
    if (c && cache.count) c.textContent = cache.count;
    setQuery('');
  }

  function handle(term) {
    if (!term.length) { restore(); return; }
    // Below the threshold we do not search — but if a search is already on screen we
    // must undo it, or the grid keeps showing hits for a term the field no longer holds.
    if (term.length < MIN_CHARS) { if (searching) restore(); return; }
    runSearch(term);
  }

  function schedule(term) {
    window.clearTimeout(timer);
    timer = window.setTimeout(function () { handle(term); }, DEBOUNCE_MS);
  }

  function bind() {
    if (bound) return;
    bound = true;

    document.addEventListener('input', function (e) {
      var field = e.target;
      if (!field || !field.matches || !field.matches(INPUT_SELECTOR) || !grid()) return;
      syncInputs(field);
      schedule(field.value.trim());
    });

    document.addEventListener('submit', function (e) {
      var form = e.target;
      if (!form || !form.matches || !form.matches('[data-et-blog-search]') || !grid()) return;
      var field = form.querySelector('input[name="q"]');
      if (!field) return;
      e.preventDefault();
      window.clearTimeout(timer);
      syncInputs(field);
      handle(field.value.trim());
    });

    document.addEventListener('click', function (e) {
      if (!searching || e.defaultPrevented || e.button || e.metaKey || e.ctrlKey || e.shiftKey) return;
      if (!e.target || !e.target.closest) return;
      var link = e.target.closest('[data-et-blog-grid] .et-blog-grid__pagination a[href]');
      if (!link) return;
      e.preventDefault();
      window.clearTimeout(timer);
      request(endpointFor(link.href, null), true);
    });
  }

  function rehydrate() {
    // setQuery() puts ?q= in the address bar. The blog template cannot read it back
    // (Liquid has no access to arbitrary query params), so without this a reloaded or
    // shared link would show the whole blog under a URL that says otherwise.
    var q;
    try { q = new URL(window.location.href).searchParams.get('q'); } catch (e) { return; }
    if (!q) return;
    q = q.trim();
    if (q.length < MIN_CHARS) return;
    var list = inputs();
    if (!list.length) return;
    for (var i = 0; i < list.length; i++) list[i].value = q;
    runSearch(q);
  }

  function init() {
    if (!grid()) return;
    bind();
    capture();
    // blog.articles_count may ignore current_tags, but the grid's data-et-blog-total
    // is paginate.items and is always filter-accurate. Reconcile on load so a
    // /tagged/ page never shows the whole-blog number next to a filtered grid.
    updateCount(grid());
    cache.count = '';
    capture();
    rehydrate();
  }

  document.addEventListener('shopify:section:load', init);
  init();
})();
